import { useCallback, useRef } from "react";

/**
 * Wraps a dialog's close handler so a whole-queue `refetch` only runs when a
 * mutation actually happened during this open — not on a read-only
 * open-and-close. Pass the returned `onMutated` to the dialog's mutation
 * callback and `onClose` to its close handler; `onClose` always runs
 * `onCloseBase` first, then conditionally refetches and clears the flag.
 */
export function useRefetchOnMutationClose(
  onCloseBase: () => void,
  refetch: () => Promise<unknown>,
  onRefetchError: () => void
): { onMutated: () => void; onClose: () => void } {
  const mutatedRef = useRef(false);

  const onMutated = useCallback(() => {
    mutatedRef.current = true;
  }, []);

  const onClose = useCallback(() => {
    onCloseBase();
    if (!mutatedRef.current) return;
    mutatedRef.current = false;
    refetch().catch(onRefetchError);
  }, [onCloseBase, refetch, onRefetchError]);

  return { onMutated, onClose };
}
